import React, { createContext, useState, useCallback } from 'react';
import { Toast, ToastType } from './ui/Toast';
import { v4 as uuidv4 } from 'uuid';

interface ToastOptions {
  message: React.ReactNode;
  type: ToastType;
  duration?: number;
  actionText?: string;
  onActionClick?: () => void;
}

interface ToastContextValue {
  addToast: (options: ToastOptions) => void;
  removeToast: (id: string) => void;
}

export const ToastContext = createContext<ToastContextValue>({
  addToast: () => {},
  removeToast: () => {},
});

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<(ToastOptions & { id: string })[]>([]);

  const addToast = useCallback((options: ToastOptions) => {
    const id = uuidv4();
    setToasts(prev => {
      // Limit maximum number of toasts
      const maxToasts = window.innerWidth < 768 ? 3 : 5;
      const newToasts = [...prev, { ...options, id }];
      return newToasts.slice(-maxToasts);
    });
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}
      <div
        className={`
          fixed z-[100] pointer-events-none
          p-4 flex flex-col items-stretch
          sm:items-start
          ${window.innerWidth < 768 ? 'bottom-0 inset-x-0' : 'bottom-4 left-4'}
        `}
        role="region"
        aria-label="Notifications"
        style={{
          maxHeight: 'calc(100vh - 100px)',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {toasts.map(toast => (
          <div 
            key={toast.id} 
            className="pointer-events-auto mb-2 last:mb-0 w-full sm:w-auto"
          >
            <Toast
              id={toast.id}
              message={toast.message}
              type={toast.type}
              duration={toast.duration}
              onDismiss={removeToast}
              actionText={toast.actionText}
              onActionClick={toast.onActionClick}
            />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};